import type { Goal } from '@medplum/fhirtypes';
import React, { useState } from 'react';

import {
  Button,
  Icon,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Stack,
  Tag,
  Text,
  useToast,
} from '@eluve/components';
import { useFhirRestRead } from '@eluve/fhir-data';
import {
  type GroupedObservations,
  formatMeasureValue,
} from '@eluve/fhir-observations';
import {
  FhirStatusTag,
  FhirVersionHistory,
  GoalAchievementTag,
  formatFhirDate,
  resourceDisplayName,
} from '@eluve/fhir-renderer';
import { useMedplum } from '@eluve/medplum-client';

import { type ReviewDecision, reviewGoal } from '../data/goals';
import {
  formatGoalTarget,
  latestTrackedValue,
  trackedObservationGroup,
  trackingLabel,
} from '../goal-list';
import {
  isPendingReview,
  readConfidence,
  readEvidence,
} from '../review-markers';

/**
 * Deep-linkable side drawer for one goal (ENG-5367): the goal details
 * (target, tracked measure, horizon, who expressed it), a proposal card
 * while the AI draft is still pending (accept / edit wording / dismiss —
 * the same transitions the backend review capability applies), and the
 * resource's `_history` audit trail (REST — the FHIR GraphQL spec has no
 * `_history`).
 */
export type GoalDetailDrawerProps = {
  goal: Goal | undefined;
  /** Observation window for the tracked-measure line; null when not loaded. */
  grouped: GroupedObservations | null;
  open: boolean;
  onClose: () => void;
  /** Called after a review decision lands so the owner can refetch reads. */
  onReviewed: () => void;
};

function confidenceTag(goal: Goal): React.ReactNode {
  const confidence = readConfidence(goal);
  if (confidence === null) return null;
  const label =
    confidence >= 0.75 ? 'High' : confidence >= 0.5 ? 'Medium' : 'Low';
  return (
    <Tag
      pill
      subtle
      size="xs"
      color={confidence >= 0.75 ? 'positive' : 'warning'}
      label={`${label} confidence`}
    />
  );
}

const ProposalCard: React.FC<{
  goal: Goal;
  onDecide: (decision: ReviewDecision) => Promise<void>;
  busy: boolean;
  canReview: boolean;
}> = ({ goal, onDecide, busy, canReview }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const evidence = readEvidence(goal);
  const statusDate = formatFhirDate(goal.statusDate);
  const target = formatGoalTarget(goal);

  const saveEdited = async () => {
    const trimmed = editValue.trim();
    if (!trimmed) return;
    await onDecide({ kind: 'accept', editedDisplay: trimmed });
  };

  return (
    <div
      data-testid="goal-proposal-card"
      className="rounded-lg border border-border-insights-subtle bg-bg-insights-subtle p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" fullWidth={false}>
        <Icon name="Sparkles" size="xs" className="text-text-insights" />
        <Text inline size="sm" weight="semibold" className="text-text-insights">
          Eluve proposed a new goal
        </Text>
        <span className="ml-auto">{confidenceTag(goal)}</span>
      </Stack>

      {editing ? (
        <Input
          value={editValue}
          onChange={(event) => setEditValue(event.target.value)}
          autoFocus
          aria-label="Corrected goal wording"
          className="mt-2 bg-bg-primary font-semibold privacy-text"
        />
      ) : (
        <Text size="md" weight="semibold" className="mt-1 privacy-text">
          {resourceDisplayName(goal)}
        </Text>
      )}

      <Text
        size="xs"
        color="tertiary"
        weight="regular"
        className="mt-1 privacy-text"
      >
        {[
          statusDate ? `Source · ${statusDate}` : undefined,
          target ? `Target ${target}` : undefined,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Text>

      {evidence && (
        <blockquote className="mt-2 rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm italic privacy-text">
          “{evidence}”
        </blockquote>
      )}

      <Stack direction="horizontal" gap={2} align="center" className="mt-3">
        {editing ? (
          <>
            <div className="flex-1" />
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={busy || !canReview || !editValue.trim()}
              onClick={() => void saveEdited()}
              data-testid="proposal-save-edited"
            >
              Save & accept
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy || !canReview}
              onClick={() => void onDecide({ kind: 'dismiss' })}
              data-testid="proposal-dismiss"
            >
              Dismiss
            </Button>
            <div className="flex-1" />
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => {
                setEditValue(resourceDisplayName(goal));
                setEditing(true);
              }}
              data-testid="proposal-edit"
            >
              Edit wording
            </Button>
            <Button
              size="sm"
              disabled={busy || !canReview}
              onClick={() => void onDecide({ kind: 'accept' })}
              data-testid="proposal-accept"
            >
              Accept
            </Button>
          </>
        )}
      </Stack>
    </div>
  );
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="flex items-baseline gap-3 py-2 not-first:border-t not-first:border-border-primary">
    <Text inline size="xs" color="tertiary" className="w-24 shrink-0">
      {label}
    </Text>
    <Text
      inline
      size="sm"
      weight="regular"
      className="min-w-0 flex-1 privacy-text"
    >
      {children}
    </Text>
  </div>
);

const GoalDetails: React.FC<{
  goal: Goal;
  grouped: GroupedObservations | null;
}> = ({ goal, grouped }) => {
  const group = trackedObservationGroup(goal, grouped);
  const tracking = trackingLabel(goal, group);
  const now = latestTrackedValue(group);
  const target = formatGoalTarget(goal);
  const due = formatFhirDate(goal.target?.[0]?.dueDate);
  const start = formatFhirDate(goal.startDate);
  const statusDate = formatFhirDate(goal.statusDate);
  const horizon = goal.category?.[0]?.text;
  const expressedBy = goal.expressedBy?.display;
  // Display strings only — no reference resolution (kept deliberately cheap).
  const addresses = (goal.addresses ?? [])
    .map((reference) => reference.display)
    .filter(Boolean)
    .join(', ');

  return (
    <div>
      <Text
        size="xs"
        color="tertiary"
        weight="medium"
        className="tracking-wide uppercase"
      >
        Details
      </Text>
      <div data-testid="goal-details">
        {tracking && (
          <DetailRow label="Tracking">
            {tracking}
            {now ? ` — now ${formatMeasureValue(now.value, group?.unit)}` : ''}
          </DetailRow>
        )}
        {target && <DetailRow label="Target">{target}</DetailRow>}
        {due && <DetailRow label="Due">{due}</DetailRow>}
        {start && <DetailRow label="Started">{start}</DetailRow>}
        {statusDate && <DetailRow label="Updated">{statusDate}</DetailRow>}
        {horizon && <DetailRow label="Horizon">{horizon}</DetailRow>}
        {expressedBy && (
          <DetailRow label="Expressed by">{expressedBy}</DetailRow>
        )}
        {addresses && <DetailRow label="Addresses">{addresses}</DetailRow>}
      </div>
    </div>
  );
};

export const GoalDetailDrawer: React.FC<GoalDetailDrawerProps> = ({
  goal,
  grouped,
  open,
  onClose,
  onReviewed,
}) => {
  const { client, practitionerId } = useMedplum();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const goalId = goal?.id;
  const goalVersion = goal?.meta?.versionId;
  const history = useFhirRestRead(
    goalId ? (medplum) => medplum.readHistory('Goal', goalId) : null,
    [goalId, goalVersion],
  );
  const versions: Goal[] = (history.data?.entry ?? []).flatMap((entry) =>
    entry.resource ? [entry.resource] : [],
  );

  const decide = async (decision: ReviewDecision) => {
    if (!client || !goalId) return;
    setBusy(true);
    try {
      await reviewGoal(client, {
        goalId,
        decision,
        reviewerReference: practitionerId
          ? `Practitioner/${practitionerId}`
          : null,
      });
      toast.success(
        decision.kind === 'dismiss' ? 'Draft dismissed.' : 'Goal accepted.',
      );
      onReviewed();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Could not save the review decision.',
      );
    } finally {
      setBusy(false);
    }
  };

  const lifecycle = goal?.lifecycleStatus;

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="right"
        className="w-[min(640px,94vw)] gap-0 overflow-y-auto sm:max-w-none"
        data-testid="goal-detail-drawer"
      >
        {goal && (
          <>
            <SheetHeader>
              <SheetTitle className="privacy-text">
                {resourceDisplayName(goal)}
              </SheetTitle>
              <SheetDescription className="privacy-text">
                {[
                  lifecycle &&
                  lifecycle !== 'active' &&
                  lifecycle !== 'accepted'
                    ? lifecycle.replace(/-/g, ' ')
                    : undefined,
                  formatFhirDate(goal.startDate)
                    ? `Since ${formatFhirDate(goal.startDate)}`
                    : undefined,
                ]
                  .filter(Boolean)
                  .join(' · ') || ' '}
              </SheetDescription>
            </SheetHeader>

            <Stack gap={6} align="stretch" className="px-4 pb-6">
              <Stack
                direction="horizontal"
                gap={2}
                align="center"
                fullWidth={false}
              >
                <GoalAchievementTag goal={goal} />
                {lifecycle && lifecycle !== 'active' && (
                  <FhirStatusTag status={lifecycle} />
                )}
              </Stack>
              {isPendingReview(goal) && (
                <ProposalCard
                  goal={goal}
                  busy={busy}
                  canReview={Boolean(client)}
                  onDecide={decide}
                />
              )}

              <GoalDetails goal={goal} grouped={grouped} />

              <div>
                <Text
                  size="xs"
                  color="tertiary"
                  weight="medium"
                  className="mb-2 tracking-wide uppercase"
                >
                  Change log & audit trail
                </Text>
                {history.loading ? (
                  <Text size="sm" color="tertiary" weight="regular">
                    Loading history…
                  </Text>
                ) : (
                  <FhirVersionHistory versions={versions} />
                )}
              </div>
            </Stack>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
